import React, { useContext, useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Context } from '../../context/Context';
import HeliosClient from '../../context/Helios';
import {
	AppContext,
	IndividualStateKey,
	ScreenIndex,
	Brands,
	Responsible,
	FieldStateKey,
	PhotoSection,
	ScreenIndexPREMIER,
	ScreenIndexOPTIMA,
	ScreenIndexMultinational,
	ScreenIndexSODA,
} from '../../context/interfaces';
import ConfirmButton from '../common/ConfirmButton';
import ProgressBar from '../ProgressBar';
import TextInput from '../ui/TextInput';
import RoundButton from '../../components/RoundButton';
import { buildFields, Field, assignDataToForm, compareOcrDataAttributes } from './Utils';
import { completeSectionGTM, initializeSectionGTM } from '../../shared/utils/gooogleTagManager';
import { BRAND, getCurrentClaimantKey, isActiveIntegrationCpe, urlIdRewrite } from 'shared/utils';
import '../../assets/styles/components/information/ClientForm.scss';
import DatePicker from 'components/common/DatePicker';
import { FormControl, FormHelperText, InputLabel, Select } from '@material-ui/core';
import OtherLocationForm from '../../components/location/other-location';
import { initializePendo } from 'pendo/pendoUtils';
import CpeClient from 'context/CPE';

interface Props {
	screenKey: ScreenIndex;
	title: string;
	to: string;
	isCounterpartInfo?: boolean;
}

// The next two functions attempt to capture the UX researched and noted here:
// https://ux.stackexchange.com/questions/74531/form-validation-when-should-error-messages-be-triggered
// https://alistapart.com/article/inline-validation-in-web-forms/

/**
 * Wrap around the fields.onChange, to add handling of UI validation state.
 * Particularly to eagerly validate the field if user corrected the value.
 */
export const buildOnChangeValidation = (field: Field, index: number, isValid: boolean[], setIsValid: any) => {
	return (event: React.ChangeEvent<HTMLInputElement>): void => {
		if (field.onChange) {
			field.onChange(event);
		}
		// Only eagerly (during change) validate if user _corrected_ form.
		const previouslyValid = isValid[index];
		const newlyValid = field.isValid ? field.isValid(event.target.value) : true;
		if (!previouslyValid && newlyValid) {
			const newIsValid = [...isValid];
			newIsValid[index] = true;
			setIsValid(newIsValid);
		}
	};
};

export const buildOnClickValidation = (field: Field, index: number, isValid: boolean[], setIsValid: any) => {
	return (value: boolean): void => {
		if (field.onClick) {
			field.onClick(value as any);
		}

		const valueString = value ? 'si' : 'no';
		const previouslyValid = isValid[index];
		const newlyValid = field.isValid ? field.isValid(valueString) : true;

		if (!previouslyValid && newlyValid) {
			const newIsValid = [...isValid];
			newIsValid[index] = true;
			setIsValid(newIsValid);
		}
	};
};

export const buildOnChangeSelectedValidation = (field: Field, index: number, isValid: boolean[], setIsValid: any) => {
	return (event: React.ChangeEvent<HTMLSelectElement>) => {
		const inputData = {
			target: { value: event.currentTarget.value },
		} as React.ChangeEvent<HTMLInputElement>;

		if (field.onChange) {
			field.onChange(inputData);
		}

		// Only eagerly (during change) validate if user _corrected_ form.
		const previouslyValid = isValid[index];
		const newlyValid = field.isValid ? field.isValid(event.target.value) : true;
		if (!previouslyValid && newlyValid) {
			const newIsValid = [...isValid];
			newIsValid[index] = true;
			setIsValid(newIsValid);
		}

		return true;
	};
};

export const buildOnBlurValidation = (
	field: Field,
	index: number,
	value: string,
	isValid: boolean[],
	setIsValid: any,
	interactedWith: boolean[],
	setInteractedWith: any,
	stateKey: IndividualStateKey
): (() => void) => {
	return (): void => {
		let hasConterpart = false;
		hasConterpart =
			[Brands.SURA, Brands.LAFISE, Brands.OCEANICA, Brands.SEGUROS_MUNDIAL, Brands.COMFENALCO].includes(
				BRAND as Brands
			) && stateKey === IndividualStateKey.COUNTERPART;
		if (hasConterpart && value && value.trim().length === 0) {
			const newIsValid = [...isValid];
			newIsValid[index] = field.required ? field.isValid(value) : true;
			setIsValid(newIsValid);
		} else {
			const newIsValid = [...isValid];
			newIsValid[index] = field.isValid ? field.isValid(value) : true;
			setIsValid(newIsValid);
		}
		const newInteractedWith = [...interactedWith];
		newInteractedWith[index] = true;
		setInteractedWith(newInteractedWith);
	};
};

const ClientForm = (props: Props): JSX.Element => {
	const context = useContext(Context) as AppContext;
	const location = useLocation();
	const { id } = useParams<{ id: string }>();
	const [initLoadGT, setInitLoadGT] = useState(true);
	const { dispatch, eventId, responsibility, ClaimantType, SinisterType, insured, photos } = context;

	const currentStateKey = getCurrentClaimantKey(BRAND as Brands, ClaimantType, props.isCounterpartInfo);
	const state = context[currentStateKey];
	const fields = buildFields(dispatch, currentStateKey, props.screenKey, SinisterType);
	const isSuraCounterpart = BRAND === Brands.SURA && currentStateKey === IndividualStateKey.COUNTERPART;
	const isNoneResponsible = responsibility?.responsible === Responsible.NONE;
	const fieldsDependency = (field: Field) => {
		if (field.dependencyKey) {
			return insured.fields[field.dependencyKey] ? insured.fields[field.dependencyKey] : false;
		}
		return false;
	};

	const initIsValid = (): boolean[] => {
		return fields.map((f) => {
			const isVisible = fieldsDependency(f);
			let valid: boolean;
			if (f.isHidden) {
				valid = !isVisible || f.isValid(f.stateSelector(state));
			} else {
				valid = (!f.required && f.stateSelector(state)?.length === 0) || f.isValid(f.stateSelector(state));
			}
			return f.readonly || valid;
		});
	};

	//  Because some fields might initially be invalid, but we dont want to show an error message
	//	too proactivly / just yet, we keep two pieces of state. Whether the user interacted with
	// 	component, and whether its valid. Condition to show error message is "already interacted"
	//	and "invalid". Condition to continue is all "valid".
	// 	These are boolean arrays, where index represents field, aligned to the 'fields' variable.
	const [interactedWith, setInteractedWith] = useState<boolean[]>(Array(fields.length).fill(false));
	const [isValid, setIsValid] = useState<boolean[]>(initIsValid()); // asume readonlys are valid
	const [redirectTo, setRedirectTo] = useState<string>(urlIdRewrite(props.to, id));

	useEffect(() => {
		if (initLoadGT) {
			initializeSectionGTM(context, location.pathname);
			setInitLoadGT(false);
		}
	}, [initLoadGT, context, location.pathname]);

	useEffect(() => {
		const validScreensCpe = [
			ScreenIndexPREMIER.INJURED,
			ScreenIndexOPTIMA.INJURED,
			ScreenIndexMultinational.INJURED,
			ScreenIndexSODA.INJURED,
		];
		const isValidScreen =
			(isActiveIntegrationCpe() && validScreensCpe.includes(props.screenKey)) ||
			(!isActiveIntegrationCpe() &&
				(props.screenKey === ScreenIndex.INSURED ||
					props.screenKey === ScreenIndex.INJURED ||
					validScreensCpe.includes(props.screenKey)));

		if (isValidScreen && state?.fields) {
			setRedirectTo(
				!state.fields.isOtherDriver
					? `/${id}/vehicle/information`
					: ScreenIndex.DRIVER === props.screenKey
						? props.to
						: `/${id}/driver/information`
			);
		}
	}, [state, setRedirectTo, currentStateKey, props.screenKey, id]);

	if (eventId === null) {
		return (
			<div className="client-form">
				<h1 className="error">Evento no existe</h1>
			</div>
		);
	}

	const canContinue =
		isSuraCounterpart && isNoneResponsible ? isNoneResponsible : initIsValid().every((boolean) => boolean === true);

	const submitForm = async (): Promise<void> => {
		const screenIndexMap = {
			[Brands.PREMIER]: {
				DRIVER: ScreenIndexPREMIER.DRIVER,
				INJURED: ScreenIndexPREMIER.INJURED,
			},
			[Brands.OPTIMA]: {
				DRIVER: ScreenIndexOPTIMA.DRIVER,
				INJURED: ScreenIndexOPTIMA.INJURED,
			},
			[Brands.MULTINATIONAL]: {
				DRIVER: ScreenIndexMultinational.DRIVER,
				INJURED: ScreenIndexMultinational.INJURED,
			},
			[Brands.SODA]: {
				DRIVER: ScreenIndexSODA.DRIVER,
				INJURED: ScreenIndexSODA.INJURED,
			},
		};

		const useCPE = BRAND in screenIndexMap;
		const screenIndexDetected = screenIndexMap[BRAND as keyof typeof screenIndexMap];
		if (
			useCPE &&
			[screenIndexDetected.INJURED, screenIndexDetected.DRIVER].includes(props.screenKey) &&
			currentStateKey !== IndividualStateKey.COUNTERPART
		) {
			assignDataToForm(dispatch, screenIndexDetected.DRIVER, ClaimantType, state);
		} else if (
			[ScreenIndex.INJURED, ScreenIndex.INSURED, ScreenIndex.DRIVER].includes(props.screenKey) &&
			currentStateKey !== IndividualStateKey.COUNTERPART
		) {
			assignDataToForm(dispatch, ScreenIndex.DRIVER, ClaimantType, state);
		}
		completeSectionGTM(context, location.pathname);

		await syncDataToCPE();
		console.log('currentStateKey', currentStateKey, location.pathname);

		try {
			await initializePendo(context);
		} catch (error) {
			console.error('Error al inicializar Pendo:', error);
		}

		await new HeliosClient().saveInspection(context, id);
	};

	const detectOCRData = (fieldKey: string, currentValue: string): any => {
		if (!context.ocrDataByTheClient) {
			return currentValue;
		}
		const ocrData = context.ocrDataByTheClient;

		if (fieldKey === FieldStateKey.LICENSE) {
			return ocrData.driverLicense ?? currentValue;
		} else if (fieldKey === FieldStateKey.FIRST_NAME) {
			if (ocrData.fullName && ocrData.fullName.length > 0) {
				let fullNameParts = ocrData.fullName?.split(' ');
				if (ocrData.fullName.indexOf(',') > -1) {
					fullNameParts = ocrData.fullName?.split(',');
					return fullNameParts[1].trimLeft();
				} else {
					return fullNameParts[0].trimLeft();
				}
			} else {
				return currentValue;
			}
		} else if (fieldKey === FieldStateKey.LAST_NAME) {
			if (ocrData.fullName && ocrData.fullName.length > 0) {
				let fullNameParts = ocrData.fullName?.split(' ');
				if (ocrData.fullName.indexOf(',') > -1) {
					fullNameParts = ocrData.fullName?.split(',');
					return fullNameParts[0].trimLeft();
				} else {
					return fullNameParts[1].trimLeft();
				}
			} else {
				return currentValue;
			}
		} else if (fieldKey === FieldStateKey.PLATE) {
			return ocrData.licensePlate ?? currentValue;
		} else if (fieldKey === FieldStateKey.MAKE) {
			return ocrData.make ?? currentValue;
		} else if (fieldKey === FieldStateKey.MODEL) {
			return ocrData.model ?? currentValue;
		} else if (fieldKey === FieldStateKey.YEAR) {
			return ocrData.year ?? currentValue;
		} else if (fieldKey === FieldStateKey.COLOR) {
			return ocrData.color ?? currentValue;
		} else if (fieldKey === FieldStateKey.DRIVER_MARBETE) {
			return ocrData.marbeteDateExpiry ?? currentValue;
		}

		return currentValue;
	};

	const syncDataToCPE = async (): Promise<void> => {
		if (!isActiveIntegrationCpe()) return;

		if (location.pathname.indexOf('vehicle/information') > -1) {
			const result = compareOcrDataAttributes(context.ocrDataByTheClient, context.ocrDataByTheProcess);
			if (result.areIdentical) {
				return;
			}

			const cpeClient = new CpeClient();

			result.differingAttributes.forEach(async (attribute: string) => {
				if (attribute === 'driverLicense') {
					const photoObj = photos.find(
						(photo) => photo.section === PhotoSection.INSURED_LICENSE || photo.section === PhotoSection.INJURED_LICENSE
					);

					await cpeClient.updateMLResult(
						context.cpeId ?? '',
						photoObj?.cpeImageId ?? '',
						'drivers_license',
						context.ocrDataByTheClient
					);
				} else if (attribute === 'licensePlate') {
					const photoObj = photos.find(
						(photo) =>
							photo.section === PhotoSection.INSURED_TABLILLA || photo.section === PhotoSection.INJURED_TABLILLA
					);

					await cpeClient.updateMLResult(
						context.cpeId ?? '',
						photoObj?.cpeImageId ?? '',
						'license_plate',
						context.ocrDataByTheClient
					);
				} else if (
					attribute === 'fullName' ||
					attribute === 'make' ||
					attribute === 'model' ||
					attribute === 'year' ||
					attribute === 'color'
				) {
					const photoObj = photos.find(
						(photo) =>
							photo.section === PhotoSection.INSURED_VEHICLE_LICENSE ||
							photo.section === PhotoSection.INJURED_VEHICLE_LICENSE
					);

					await cpeClient.updateMLResult(
						context.cpeId ?? '',
						photoObj?.cpeImageId ?? '',
						'car_registration',
						context.ocrDataByTheClient
					);
				}
			});
		}
	};

	return (
		<div className="client-form">
			{props.screenKey && <ProgressBar screenIndex={props.screenKey} />}
			<div className="fields-container">
				<div>
					{fields.map((field, index) => {
						const onChange = buildOnChangeValidation(field, index, isValid, setIsValid);
						const value = detectOCRData(field.fieldKey, field.stateSelector(state));
						const onBlur = buildOnBlurValidation(
							field,
							index,
							value,
							isValid,
							setIsValid,
							interactedWith,
							setInteractedWith,
							currentStateKey
						);
						const onClick = buildOnClickValidation(field, index, isValid, setIsValid);
						const isVisible = fieldsDependency(field);

						if (field.type === 'checkbox') {
							return (
								<label htmlFor={field.label} className="checkbox-form" key={field.label}>
									{field.label}

									<input
										id={field.label}
										type="checkbox"
										onChange={onChange}
										checked={value}
										className="checkbox-input"
									/>
									<span className="checkmark-input"></span>
								</label>
							);
						} else if (field.type === 'location') {
							return (
								<div style={{ paddingBottom: '0.3rem' }} key={field.label}>
									<OtherLocationForm
										brand={BRAND}
										hiddenField={true}
										stateKey={IndividualStateKey.INSURED}
										level1={FieldStateKey.DEPARTMENT}
										level2={FieldStateKey.CITY}
									/>
								</div>
							);
						} else if (field.type === 'selected' && !field.isHidden) {
							const onChangeSelected = buildOnChangeSelectedValidation(field, index, isValid, setIsValid);
							return (
								<div key={field.label} className="select-input">
									<FormControl variant="outlined" error={interactedWith[index] && !isValid[index]}>
										<InputLabel htmlFor="outlined-age-native-simple">{field.label}</InputLabel>
										<Select
											native
											value={value}
											onChange={(e) =>
												onChangeSelected({
													currentTarget: { type: 'selected', value: e.target.value },
													target: { type: 'selected', value: e.target.value },
												} as any)
											}
											onBlur={onBlur}
											label={field.label}
										>
											{field.selectedData &&
												field.selectedData.map((d) => {
													if (d.name) {
														return (
															<option key={d.id} value={d.id}>
																{d.name}
															</option>
														);
													} else {
														return <option key={d.id}></option>;
													}
												})}
										</Select>
										{interactedWith[index] && !isValid[index] && <FormHelperText>{field.errorMessage}</FormHelperText>}
									</FormControl>
								</div>
							);
						} else if (field.type === 'selected' && field.dependencyKey && isVisible) {
							const onChangeSelected = buildOnChangeSelectedValidation(field, index, isValid, setIsValid);
							return (
								<div key={field.label} className="select-input" style={{ marginBottom: '1rem' }}>
									<FormControl variant="outlined" error={interactedWith[index] && !isValid[index]}>
										<InputLabel htmlFor="outlined-age-native-simple">{field.label}</InputLabel>
										<Select
											native
											value={value}
											onChange={(e) =>
												onChangeSelected({
													currentTarget: { type: 'selected', value: e.target.value },
													target: { type: 'selected', value: e.target.value },
												} as any)
											}
											onBlur={onBlur}
											label={field.label}
										>
											{field.selectedData &&
												field.selectedData.map((d) => {
													if (d.name) {
														return (
															<option key={d.id} value={d.id}>
																{d.name}
															</option>
														);
													} else {
														return <option key={d.id}></option>;
													}
												})}
										</Select>
										{interactedWith[index] && !isValid[index] && <FormHelperText>{field.errorMessage}</FormHelperText>}
									</FormControl>
								</div>
							);
						} else if (field.type === 'date') {
							return (
								<div className="date-input" key={index}>
									<DatePicker
										label={field.label}
										value={value || null}
										onChange={(v: any) => {
											onChange({ target: { type: 'date', value: v } } as any);
										}}
									/>
								</div>
							);
						} else if (field.type === 'roundButton') {
							return (
								<div className="responsibility-screen section-from" key={index}>
									<p>{field.label}</p>
									<div className="legal-assistance-options">
										<RoundButton checked={!!value} onClick={(): void => onClick(true)}>
											Sí
										</RoundButton>
										<RoundButton checked={value === false} onClick={(): void => onClick(false)}>
											No
										</RoundButton>
									</div>
								</div>
							);
						} else if (field.dependencyKey && isVisible) {
							const errorMessage = isValid[index] || !interactedWith[index] ? undefined : field.errorMessage;
							return (
								<TextInput
									key={field.label}
									label={field.label}
									type={field.type}
									inputMode={field.inputMode}
									inputSize={field.inputSize}
									readonly={field.readonly}
									required={true}
									value={value}
									message={errorMessage}
									onChange={onChange}
									onBlur={onBlur}
								/>
							);
						} else if (!field.isHidden) {
							const errorMessage = isValid[index] || !interactedWith[index] ? undefined : field.errorMessage;
							return (
								<TextInput
									key={field.label}
									label={field.label}
									type={field.type}
									inputMode={field.inputMode}
									inputSize={field.inputSize}
									readonly={field.readonly}
									required={field.required}
									value={value}
									message={errorMessage}
									onChange={onChange}
									onBlur={onBlur}
								/>
							);
						}
					})}
				</div>
				<div>
					<ConfirmButton to={redirectTo} disabled={!canContinue} onClick={submitForm} />
				</div>
			</div>
		</div>
	);
};

export default ClientForm;
